import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Checkbox,
  Button,
  Divider,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import Ask7G from "../../assets/client/magic-icon.svg";
import visibilityIcon from "../../assets/interaction-detail-page/view-icon.svg";
import "../../styles/common.css";
import { getFileExtension } from "../../utils/utils";

interface AiFileProcessingModalProps {
  open: boolean;
  onClose: () => void;
  file?: any;
  onProcess: (selectedOptions: string[]) => void;
  isSubmitting?: boolean;
}

interface AnalysisOption {
  id: string;
  title: string;
  description: string;
}

const analysisOptions: AnalysisOption[] = [
  {
    id: "transcript",
    title: "Transcript Analysis",
    description:
      "Extract key topics, speakers, and summarize the conversation flow from audio/video transcripts.",
  },
  {
    id: "slide",
    title: "Slide Analysis",
    description: "Identify slides & summary from presentation screens.",
  },
];

const AiFileProcessingModal: React.FC<AiFileProcessingModalProps> = ({
  open,
  onClose,
  file,
  onProcess,
  isSubmitting = false,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedOptions([]);
    }
  }, [open, file]);

  const handleCloseModal = () => {
    if (isSubmitting) return;
    setSelectedOptions([]);
    onClose();
  };

  const handleToggleOption = (id: string) => {
    setSelectedOptions((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleProcess = () => {
    onProcess(selectedOptions);
  };

  const fileName = file?.asset_name || file?.file_name || file?.name || "";
  const fileExtension = getFileExtension(fileName);
  const videoExtensions = ["mp4"];
  const isVideo =
    videoExtensions.includes(fileExtension) ||
    file?.c_type?.toLowerCase()?.includes("video") ||
    file?.assetType?.toLowerCase()?.includes("video");

  const displayedOptions = isVideo
    ? analysisOptions
    : analysisOptions.filter((opt) => opt.id !== "slide");

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
      className="ai-processing-modal"
    >
      {/* Modal Header */}
      <Box className="ai-processing-modal-header">
        <Box>
          <Typography className="ai-processing-modal-title">
            AI Processing
          </Typography>
          <Typography className="ai-processing-modal-subtitle">
            Configure how AI should process this file
          </Typography>
        </Box>
        <IconButton
          onClick={handleCloseModal}
          disabled={isSubmitting}
          size="small"
          sx={{
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            color: "#4B5563",
            "&:hover": { backgroundColor: "#F3F4F6" },
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Modal Body */}
      <DialogContent className="ai-processing-modal-body">
        <Box sx={{ mb: 2 }}>
          <Typography className="ai-processing-section-title">
            Standard AI Processing
          </Typography>
          <Typography className="ai-processing-section-subtitle">
            Select one or more analysis types to process your attached file
          </Typography>
        </Box>

        {/* Options List */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
          {displayedOptions.map((opt) => {
            const isChecked = selectedOptions.includes(opt.id);
            return (
              <Box
                key={opt.id}
                onClick={() => handleToggleOption(opt.id)}
                className={`ai-processing-card ${isChecked ? "ai-processing-card-active" : ""}`}
              >
                <Checkbox
                  checked={isChecked}
                  onChange={() => handleToggleOption(opt.id)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    p: 0,
                    mt: 0.2,
                    color: "#D1D1DB",
                    backgroundColor: "#FFFFFF",
                    "&.Mui-checked": {
                      color: "#83018F",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 22,
                      borderRadius: "6px",
                    },
                  }}
                />
                <Box>
                  <Typography className="ai-processing-card-title">
                    {opt.title}
                  </Typography>
                  <Typography className="ai-processing-card-desc">
                    {opt.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      {/* Divider line right above footer */}
      <Divider sx={{ borderColor: "#E5E7EB" }} />

      {/* Modal Footer */}
      <Box className="ai-processing-modal-footer">
        <Box className="ai-processing-visible-badge">
          <img src={visibilityIcon} alt="visible icon" />
          Visible to Everyone
        </Box>

        <Button
          onClick={handleProcess}
          variant="contained"
          disabled={selectedOptions.length === 0 || isSubmitting}
          className="ai-processing-btn"
          startIcon={
            <img
              src={Ask7G}
              alt="AI"
              style={{ width: "17px", height: "17px", filter: "brightness(0) invert(1)" }}
            />
          }
        >
          Process with AI
        </Button>
      </Box>
    </Dialog>
  );
};

export default AiFileProcessingModal;
